<template>
  <div class="page">
    <n-loading-spinner v-if="loading" />
    <section class="section content-container" v-if="!loading">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column">
            <div class="island">
              <nav class="level">
                <div class="level-left"></div>
                <div class="level-right">
                  <p class="level-item">
                    <n-button @click="addFlow()" variant="primary">
                      <span>Add Flow</span>
                    </n-button>
                  </p>

                  <p class="level-item">
                    <n-button @click="openFileSelector()" variant="primary">
                      <span>Import flow</span>
                      <input
                        ref="importFlowButton"
                        id="file-picker"
                        type="file"
                        accept=".json"
                        @change="onFileChange"
                      />
                    </n-button>
                  </p>
                </div>
              </nav>

              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th class="icon-column">
                      <n-tooltip position="bottom" :text="flowPriorityTooltip" :offset="2" />
                    </th>
                    <th class="icon-column">&nbsp;</th>
                    <th>Name</th>
                    <th class="status-column">Status</th>
                    <th class="has-text-right count-percent-column">Started</th>
                    <th class="has-text-right count-percent-column">Engaged</th>
                    <th class="has-text-right count-percent-column">Completed</th>
                    <th></th>
                  </tr>
                </thead>
                <draggable id="flows-list" @end="updatePriorities" v-model="botFlows" tag="tbody" item-key="id">
                  <template #item="{ element: flow, index }">
                    <tr class="flow-row" :key="flow.id">
                      <td class="icon-column">
                        <span class="icon draggable">
                          <DragIcon color="#A9A9A9" class="icon-1-5x draggable" />
                        </span>
                      </td>

                      <td class="icon-column trigger-icon">
                        <n-tooltip position="bottom" :text="'Trigger: ' + getTriggerDescription(flow)">
                          <span class="icon">
                            <svg v-if="getTriggerIcon(flow).startsWith('#')" class="custom-icon">
                              <use
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                :xlink:href="`${getTriggerIcon(flow)}`"
                              ></use>
                            </svg>
                            <font-awesome-icon
                              v-if="!getTriggerIcon(flow).startsWith('#') && getTriggerIcon(flow)"
                              :icon="['far', getTriggerIcon(flow)]"
                            ></font-awesome-icon>
                          </span>
                        </n-tooltip>
                      </td>
                      <td>
                        <router-link :to="{ name: 'bots-flow-designer', params: { flowId: flow.id } }">{{
                          flow.name
                        }}</router-link>
                      </td>
                      <td>
                        <n-badge v-if="flow.status === 'live'" variant="success">Live</n-badge>
                        <n-badge v-if="flow.status === 'paused'" variant="secondary">Paused</n-badge>
                        <n-badge v-if="flow.status === 'draft'" variant="default">Draft</n-badge>
                      </td>
                      <td class="has-text-right stat started">
                        {{ flow.startedCount || 0 }}
                      </td>
                      <td class="has-text-right stat engaged">
                        {{ calculatePercentage(flow.engagedCount, flow.startedCount) }}%
                      </td>
                      <td class="has-text-right stat completed">
                        {{ calculatePercentage(flow.completedCount, flow.startedCount) }}%
                      </td>
                      <td id="drop-down-menu" class="menu-icon action">
                        <DropDownMenu :goLeft="true" dropDownIcon="dots">
                          <a id="export-flow-button" @click="exportFlow(flow)" class="dropdown-item">
                            <ExportIcon></ExportIcon>
                            Export
                          </a>
                          <a id="clone-flow-button" @click="cloneFlow(flow, index)" class="dropdown-item">
                            <CloneIcon></CloneIcon>
                            Clone
                          </a>
                          <a @click="deleteFlow(flow)" class="dropdown-item">
                            <DeleteIcon></DeleteIcon>
                            Delete
                          </a>
                        </DropDownMenu>
                      </td>
                    </tr>
                  </template>
                </draggable>
              </table>

              <AddFlowModal
                v-if="showModal"
                @close="showModal = false"
                @flow-added="(flow: FlowListItemViewModel) => chooseFlow(flow, true)"
              ></AddFlowModal>

              <Modal v-if="showDeleteModal" v-on:cancel="showDeleteModal = false">
                <template v-slot:title>
                  <p class="modal-card-title">Delete Flow</p>
                </template>

                <template v-slot:body>
                  <label class="label"
                    >Are you sure you want to delete the <b>{{ flowToDelete?.name }}</b> flow? The action is
                    irreversible. Please type the name of the flow to confirm:</label
                  >
                  <input class="input" type="text" v-model="flowName" />
                </template>

                <template v-slot:buttons>
                  <n-button :disabled="flowName !== flowToDelete?.name" variant="primary" @click="deleteFlowConfirm"
                    >Delete</n-button
                  >
                  <n-button @click="showDeleteModal = false">Cancel</n-button>
                </template>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import Draggable from 'vuedraggable';
import { UpdateBotFlowsPriorityViewModel } from '@/features/bots/models/request/updateBotFlowsPriorityViewModel';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import Modal from '@/shared/components/Modal.vue';
import { AvailableTriggers, FlowViewModel } from '@/features/bots/models/designer';
import Toast from '@/shared/services/toast';
import { exportFlowHelper } from '@/features/bots/services/exportFlow';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import DragIcon from '@/shared/components/DragIcon.vue';
import Button from '@/shared/designsystem/components/control/button/button.vue';
import HelpIcon from 'vue-material-design-icons/HelpCircleOutline.vue';
import CloneIcon from 'vue-material-design-icons/ContentCopy.vue';
import ExportIcon from 'vue-material-design-icons/Export.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import AddFlowModal from '@/shared/components/AddFlowModal.vue';
import { flowsApi } from '@/features/bots/services/flows';
import appInsights from '@/shared/services/appInsights';
import { SET_FLOWS } from '@/store/bot-designer/mutationTypes';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const loadFlows = (forceLoad: boolean = false) => store.dispatch(LOAD_FLOWS, forceLoad);
const setFlows = (flows: FlowListItemViewModel[]) => store.commit(SET_FLOWS, flows);

const loading = ref(false);
const showModal = ref(false);
const showDeleteModal = ref(false);
const flowName = ref('');
const flowToDelete = ref<FlowListItemViewModel>();
const botFlows = ref<FlowListItemViewModel[]>([]);
const importFlowButton = ref<typeof Button | null>(null);

onMounted(() => getBotFlows());

const flowPriorityTooltip = `Order your flows by priority.
<br/><br/>
If multiple flows share the same trigger conditions, the one with the highest priority will be shown to visitors.
<br/><br/>
If none of the flows are triggered, a bot task will respond instead.`;

async function updatePriorities() {
  setPriorities();
  const request = createUpdateFlowsPrioritiesRequest();
  const updateBotFlows: UpdateBotFlowsPriorityViewModel = {
    flows: request,
  };
  await flowsApi.updateFlows(currentWorkspace.value.id, updateBotFlows);
  setFlows(botFlows.value);
}

function addFlow() {
  showModal.value = true;
  flowName.value = '';
}

function chooseFlow(flow: FlowListItemViewModel, created = false) {
  router.push({ name: 'bots-flow-designer', params: { flowId: flow.id }, query: { created: created.toString() } });
}

function onFileChange(event: any) {
  appInsights.trackEvent({ name: 'Import flow clicked' }, { workspaceId: currentWorkspace.value.id });
  const files = event.target.files;
  if (!files.length) {
    Toast.showError('No file selected');
    throw new Error('No file selected');
  }
  const file = event.target.files[0];
  handleFlowFile(file);
}

function handleFileLoad(event: any) {
  try {
    const botFlowAsJson = JSON.parse(event.target.result);
    importFlow(currentWorkspace.value.id, botFlowAsJson);
  } catch (e) {
    Toast.showError('The file must contain valid JSON');
  }
}

async function deleteFlow(flow: FlowListItemViewModel) {
  try {
    const fullFlowDetails = await flowsApi.getFlow(currentWorkspace.value.id, flow.id);
    if (!fullFlowDetails) {
      Toast.showError(`Unable to delete ${flow.name}`);
      return;
    }

    if (isTransitionedTo(fullFlowDetails)) {
      Toast.showError('Cannot delete a flow that is transitioned to by another flow or FAQ');
    } else {
      flowName.value = '';
      flowToDelete.value = flow;
      showDeleteModal.value = true;
    }
  } catch {
    Toast.showError(`Unable to delete ${flow.name}`);
  }
}

async function cloneFlow(flow: FlowListItemViewModel, index: number) {
  try {
    appInsights.trackEvent({ name: 'Clone flow clicked' }, { workspaceId: currentWorkspace.value.id, flowId: flow.id });
    const clonedFlow = await flowsApi.cloneFlow(currentWorkspace.value.id, flow.id);
    insertInBotFlows(index + 1, clonedFlow);
    await updatePriorities();
    Toast.showSuccess('Successfully cloned flow');
  } catch {
    Toast.showError('Error duplicating flow');
  }
}

function openFileSelector() {
  importFlowButton.value?.click();
}

async function deleteFlowConfirm() {
  if (!flowToDelete.value) return;

  appInsights.trackEvent({ name: 'Delete flow clicked' });
  showDeleteModal.value = false;
  try {
    await flowsApi.deleteFlow(currentWorkspace.value.id, flowToDelete.value.id);
    botFlows.value = botFlows.value.filter((botflow: FlowListItemViewModel) => botflow.id !== flowToDelete.value?.id);
    setFlows(botFlows.value);
    Toast.showSuccess(`Successfully deleted ${flowToDelete.value.name}`);
  } catch (e) {
    Toast.showError(`Unable to delete ${flowToDelete.value.name}`);
  }
}

function getTriggerDescription(flow: FlowListItemViewModel) {
  const trigger = getTriggerItem(flow);
  if (!trigger) {
    return 'None';
  }
  return trigger.description;
}

function getTriggerIcon(flow: FlowListItemViewModel) {
  const trigger = getTriggerItem(flow);
  if (!trigger) {
    return '';
  }
  return trigger.icon;
}

async function exportFlow(flow: FlowListItemViewModel) {
  try {
    await exportFlowHelper.exportFlow(currentWorkspace.value.id, flow.id);
  } catch (e) {
    Toast.showError(`There was a problem exporting ${flow.name}`);
  }
}

function calculatePercentage(value: number, total: number) {
  if (!total) {
    return 0;
  }
  return Math.round((value / total) * 100);
}

function createUpdateFlowsPrioritiesRequest() {
  return botFlows.value.map((flow: FlowListItemViewModel) => ({ id: flow.id, priority: flow.priority }));
}

function insertInBotFlows(index: number, flow: FlowListItemViewModel) {
  botFlows.value.splice(index, 0, flow);
}

function getTriggerItem(flow: FlowListItemViewModel) {
  if (flow.triggers.length === 0) {
    return null;
  }

  return AvailableTriggers.get(flow.triggers[0]);
}

function handleFlowFile(file: any) {
  if (file.type === 'application/json') {
    const reader = new FileReader();
    reader.onload = handleFileLoad;
    reader.readAsText(file);
  } else {
    Toast.showError('File must be JSON');
  }
}

async function importFlow(botId: string, botFlow: string) {
  try {
    const importedFlow = await flowsApi.importFlow(botId, { flow: botFlow });
    botFlows.value.push(importedFlow);
    router.push({ name: 'bots-flow-designer', params: { flowId: importedFlow.id } });
    Toast.showSuccess('File imported successfully');
  } catch (e: any) {
    if (e.status === 409) {
      Toast.showError(e.message);
      return;
    }
    Toast.showError('The Flow you imported is not valid');
  }
}

function setPriorities() {
  for (let i = 0; i < botFlows.value.length; i++) {
    botFlows.value[i].priority = botFlows.value.length - i;
  }
}

async function getBotFlows() {
  loading.value = true;
  try {
    const botFlowDataPromise = loadFlows(true);
    const loadedBotFlows: FlowListItemViewModel[] = await botFlowDataPromise;
    loadedBotFlows.sort((first, second) => second.priority - first.priority);
    botFlows.value = loadedBotFlows;
  } finally {
    loading.value = false;
  }
}

function isTransitionedTo(fullFlowDetails: FlowViewModel) {
  return hasFlowParent(fullFlowDetails) || hasAnswerParent(fullFlowDetails);
}

function hasFlowParent(fullFlowDetails: FlowViewModel) {
  return fullFlowDetails.parentFlows.length > 0;
}

function hasAnswerParent(fullFlowDetails: FlowViewModel) {
  return fullFlowDetails.parentAnswers.length > 0;
}
</script>
<style scoped lang="scss">
input[type='file'] {
  display: none;
}

.menu-icon {
  cursor: pointer;
}

.trigger-icon {
  color: $color-netcall-deep-sea;
}

.custom-icon {
  height: 16px;
  fill: $color-netcall-deep-sea;
}

table {
  td {
    vertical-align: middle;
    &.action {
      .dropdown {
        float: right;
      }
    }
  }
}

.icon-column {
  width: 1em;
}

.status-column {
  width: 5em;
}

.count-percent-column {
  width: 8em;
}

.stat {
  font-weight: 600;
}

.engaged {
  color: $color-netcall-salmon;
}

.completed {
  color: $color-netcall-deep-sea;
}

.flow-row {
  .draggable {
    cursor: move;
    visibility: hidden;
  }

  &:hover {
    .draggable {
      visibility: visible;
    }
  }
}

#drop-down-menu {
  span {
    padding-right: 30px;
  }
}
</style>
